import React, { useRef, useEffect } from "react";
import {
  Typography,
  Box,
  Button,
  Paper,
  IconButton,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import KeyboardArrowDown from "@mui/icons-material/KeyboardArrowDown";

import colors from "../../../Constants/colors";
import { CardDoc } from "./CardDoc";
import { EmptyState } from "./EmptyState";
import { AnnexDropZone } from "./AnnexDropZone";
import { DraggableAnnexItem } from "./DraggableAnnexItem";
import { useTranslation } from "react-i18next";
import { AnnexDocument } from "../../../Core/useAnnexActions";
import { PageRange } from "../../../Models/evaluation";

interface AnnexSectionProps {
  orderedAnnex: AnnexDocument[];
  onRemoveDocument: (id: number) => void;
  onUpdateDocument: (
    id: number,
    updates: { name?: string; pageRanges?: PageRange[] | null }
  ) => void;
  onAddSection: (
    type: "title" | "empty_section",
    options?: {
      title?: string;
      placeholder_id?: string;
      order?: number;
    }
  ) => void;
  draggedDoc: AnnexDocument | null;
  maxShown?: number;
}

export function AnnexSection({
  orderedAnnex,
  onRemoveDocument,
  onUpdateDocument,
  onAddSection,
  draggedDoc,
  maxShown = 12,
}: AnnexSectionProps) {
  const { t } = useTranslation("evaluationPage");
  const [isMenuOpen, setIsMenuOpen] = React.useState(false);
  const [isExpanded, setIsExpanded] = React.useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  const displayedAnnex = isExpanded
    ? orderedAnnex
    : orderedAnnex.slice(0, maxShown);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsMenuOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleMenuClose = () => {
    setIsMenuOpen(false);
  };

  return (
    <>
      <Box display="flex" justifyContent="space-between" mb={1}>
        <Typography
          variant="subtitle2"
          sx={{ color: colors.gray700, fontWeight: 600 }}
        >
          {t("evaluationPage.inAnnex")}
        </Typography>

        <Box sx={{ position: "relative" }} ref={menuRef}>
          <Button
            size="small"
            variant="outlined"
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            endIcon={
              <KeyboardArrowDown
                sx={{
                  transform: isMenuOpen ? "rotate(180deg)" : "none",
                  transition: "transform 0.2s",
                }}
              />
            }
            sx={{
              fontSize: "0.75rem",
              borderColor: colors.gray200,
              color: colors.primary_dark,
              textTransform: "none",
              bgcolor: colors.white,
              "&:hover": {
                bgcolor: colors.gray50,
                borderColor: colors.primary_dark,
              },
            }}
          >
            {t("evaluationPage.addSection")}
          </Button>

          {isMenuOpen && (
            <Paper
              elevation={3}
              sx={{
                position: "absolute",
                zIndex: 10,
                right: 0,
                mt: 0.5,
                minWidth: 200,
                border: `1px solid ${colors.gray300}`,
                borderRadius: 2,
              }}
            >
              <Box
                onClick={() => {
                  onAddSection("empty_section");
                  handleMenuClose();
                }}
                sx={{
                  px: 2,
                  py: 1.5,
                  cursor: "pointer",
                  bgcolor: "white",
                  "&:hover": {
                    bgcolor: colors.gray50,
                  },
                  borderTopLeftRadius: 2,
                  borderTopRightRadius: 2,
                }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: 500,
                    color: colors.gray900,
                  }}
                >
                  {t("evaluationPage.addEmpty")}
                </Typography>
              </Box>
              <Box
                onClick={() => {
                  onAddSection("title");
                  handleMenuClose();
                }}
                sx={{
                  px: 2,
                  py: 1.5,
                  cursor: "pointer",
                  bgcolor: "white",
                  "&:hover": {
                    bgcolor: colors.gray50,
                  },
                  borderBottomLeftRadius: 2,
                  borderBottomRightRadius: 2,
                }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: 500,
                    color: colors.gray900,
                  }}
                >
                  {t("evaluationPage.addTitle")}
                </Typography>
              </Box>
            </Paper>
          )}
        </Box>
      </Box>

      {orderedAnnex.length === 0 ? (
        <AnnexDropZone>
          <EmptyState text={t("evaluationPage.none")} />
        </AnnexDropZone>
      ) : (
        <>
          <Box sx={{ display: "flex", flexDirection: "column" }}>
            {displayedAnnex.map((d) => (
              <React.Fragment key={d.id || d.name}>
                <DraggableAnnexItem doc={d} showSubsectionHint={!!draggedDoc}>
                  <CardDoc
                    key={d.id || d.name}
                    doc={d}
                    onCancel={() => d.id && onRemoveDocument(d.id)}
                    editable
                    onEditDone={(name) =>
                      d.id && onUpdateDocument(d.id, { name })
                    }
                    onPageRangesChange={(ranges) =>
                      d.id && onUpdateDocument(d.id, { pageRanges: ranges })
                    }
                  />
                </DraggableAnnexItem>
                {d.subsections && d.subsections.length > 0 && (
                  <Box
                    sx={{
                      pl: 5,
                      display: "flex",
                      flexDirection: "column",
                    }}
                  >
                    {d.subsections.map((sub) => (
                      <DraggableAnnexItem
                        key={sub.id || sub.name}
                        doc={sub}
                        isSubsection
                      >
                        <CardDoc
                          key={sub.id || sub.name}
                          doc={sub}
                          onCancel={() => sub.id && onRemoveDocument(sub.id)}
                          editable
                          onEditDone={(name) =>
                            sub.id && onUpdateDocument(sub.id, { name })
                          }
                          onPageRangesChange={(ranges) =>
                            sub.id &&
                            onUpdateDocument(sub.id, { pageRanges: ranges })
                          }
                        />
                      </DraggableAnnexItem>
                    ))}
                  </Box>
                )}
              </React.Fragment>
            ))}
          </Box>

          {orderedAnnex.length > maxShown && (
            <Box display="flex" justifyContent="center" mt={1}>
              <IconButton
                size="small"
                onClick={() => setIsExpanded(!isExpanded)}
                sx={{ color: colors.accent, "&:hover": { bgcolor: colors.gray100 } }}
              >
                {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              </IconButton>
            </Box>
          )}
        </>
      )}

    </>
  );
}
